import React from 'react';

import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircleFilled } from '@tabler/icons-react';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useCreateStripeTerminalReader from 'hooks/stripe/useCreateStripeTerminalReader';
import notificationHandler from 'utils/notifications';

interface StripeTerminalReaderRegisterModalProps {
  opened: boolean;
  onClose: () => void;
}

const StripeTerminalReaderRegisterModal = ({
  opened,
  onClose,
}: StripeTerminalReaderRegisterModalProps): JSX.Element => {
  const theme = useMantineTheme();

  const { successNotification, errorNotification } = notificationHandler();
  const { trackCardReaderRegistered, trackCardReaderRegistrationFailed } = useStaffTracking();
  const form = useForm({ initialValues: { registrationCode: '' } });
  const createMutation = useCreateStripeTerminalReader();

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  const handleRegister = (): void => {
    createMutation.mutate(
      { registrationCode: form.values.registrationCode },
      {
        onSuccess: response => {
          trackCardReaderRegistered({ device_type: response.data.deviceType });
          successNotification('Reader registered', 'Your card reader has been successfully registered.');
          handleClose();
        },
        onError: error => {
          trackCardReaderRegistrationFailed({
            failure_code: error?.response?.status ? String(error.response.status) : null,
          });
          errorNotification('Registration failed', error?.response?.data.message);
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Title order={3} align='center'>
          Register Stripe Reader
        </Title>
      }
      centered
      size='lg'
      padding='xl'
    >
      <Stack spacing='md'>
        <TextInput
          label={
            <Group spacing='xs'>
              <Text>Reader Identifier</Text>
              <Tooltip
                maw={300}
                label='Power on your Stripe reader and look for the pairing code on the device screen. Enter that code here to connect the reader.'
                multiline
                position='right'
                zIndex={9999}
              >
                <ActionIcon color='kiloBlue'>
                  <IconInfoCircleFilled size={theme.fontSizes.xl} />
                </ActionIcon>
              </Tooltip>{' '}
            </Group>
          }
          placeholder='Enter the code shown on your reader'
          {...form.getInputProps('registrationCode')}
        />

        <Group position='center' mt='xl'>
          <Button
            onClick={handleRegister}
            loading={createMutation.isLoading}
            disabled={!form.values.registrationCode.trim()}
          >
            Register Card Reader
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StripeTerminalReaderRegisterModal;
